import { ClipboardList } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourseSignups, getCourses } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';
import { StudentStatusPageShell } from '../../components/dashboard/StudentStatusPageShell';
import { formatDate } from '../../utils/date';

function formatStudentName(item) {
  return item?.fullName || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || '-';
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeStatus(value) {
  return normalizeText(value).replace(/[_\s]+/g, '-');
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getBreakDate(item) {
  return toDate(item?.learningBreakAt)
    || toDate(item?.breakStartedAt)
    || toDate(item?.updatedAt)
    || toDate(item?.createdAt)
    || null;
}

export function LearningBreakStudentsPage() {
  const { activeTenantId } = useTenant();
  const [loading, setLoading] = useState(true);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const load = useCallback(async () => {
    if (!activeTenantId) {
      setSignups([]);
      setCourses([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [nextSignups, nextCourses] = await Promise.all([
        getCourseSignups(activeTenantId),
        getCourses(activeTenantId),
      ]);
      setSignups(nextSignups);
      setCourses(nextCourses);
    } catch (error) {
      console.error(error);
      setSignups([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const courseLookup = useMemo(
    () => new Map(courses.map((course) => [course.id, course])),
    [courses],
  );

  const rows = useMemo(() => {
    const search = normalizeText(searchTerm);

    return signups
      .filter((item) => normalizeStatus(item.status) === 'learning-break')
      .map((item) => ({
        ...item,
        courseName: item.courseName || courseLookup.get(item.courseId)?.name || '-',
        breakDate: getBreakDate(item),
        breakReason: item.learningBreakReason || item.breakReason || item.note || '',
        breakUntil: item.learningBreakUntil || item.expectedReturnDate || '',
      }))
      .filter((item) => {
        if (!search) return true;
        return [
          formatStudentName(item),
          item.courseName,
          item.intakeLabel,
          item.breakReason,
          item.email,
          item.phone,
        ].some((value) => normalizeText(value).includes(search));
      })
      .sort((a, b) => {
        const left = a.breakDate || new Date(0);
        const right = b.breakDate || new Date(0);
        return right - left;
      });
  }, [courseLookup, searchTerm, signups]);

  const studentsWithReturnDates = rows.filter((item) => item.breakUntil).length;
  const insightCards = [
    { label: 'Learners on pause', value: rows.length, note: 'Students taking time away from learning.', tone: 'pause' },
    { label: 'Return dates set', value: studentsWithReturnDates, note: 'Learners with a planned return date.', tone: 'blue' },
    { label: 'Courses affected', value: new Set(rows.map((item) => item.courseName).filter(Boolean)).size, note: 'Learning paths needing follow-up.', tone: 'green' },
  ];

  if (loading) {
    return <LoadingState label="Loading learning breaks..." />;
  }

  return (
    <StudentStatusPageShell
      title="Learning Breaks"
      description="Support students who are currently taking a learning break, keep their context close, and make returning easier."
      kicker="Students"
      stats={[]}
      insightCards={insightCards}
      className="learning-breaks-page"
      tableTitle="Learning Breaks"
      tableMeta={`${rows.length} students on break`}
      tableDescription="Students currently away from active learning."
      searchId="learning-break-search"
      searchValue={searchTerm}
      onSearchChange={(event) => setSearchTerm(event.target.value)}
      searchPlaceholder="Search student, course, reason..."
      rows={rows}
      columns={[
        {
          key: 'student',
          label: 'Student',
          render: (item) => (
            <div className="appsStudentCell">
              <div className="application-avatar" aria-hidden="true">
                {String(formatStudentName(item)).trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'ST'}
              </div>
              <div className="application-applicant-copy">
                <strong>{formatStudentName(item)}</strong>
                <span>{item.intakeLabel || 'Open intake'}</span>
              </div>
            </div>
          ),
        },
        { key: 'courseName', label: 'Course', render: (item) => item.courseName || '-' },
        { key: 'intakeLabel', label: 'Cohort', render: (item) => item.intakeLabel || 'Open intake' },
        { key: 'breakDate', label: 'Break Started', render: (item) => (item.breakDate ? formatDate(item.breakDate) : '-') },
        { key: 'breakUntil', label: 'Expected Return', render: (item) => (item.breakUntil ? formatDate(item.breakUntil) : '-') },
        { key: 'breakReason', label: 'Reason', render: (item) => item.breakReason || '-' },
        {
          key: 'view',
          label: 'View student',
          render: (item) => (
            <Button to={`/view-student?applicationId=${encodeURIComponent(item.id)}`} variant="secondary" className="appsViewBtn">
              View
            </Button>
          ),
        },
      ]}
      empty={(
        <EmptyState
          title="No students on learning break yet."
          message="Students placed on learning break will appear here."
          icon={ClipboardList}
        />
      )}
    />
  );
}
